/**
 * Renewal queue membership, shared by the Renewals page and the left-nav
 * count (ADO #1597) so the badge and the table cannot disagree.
 *
 * A group is in the queue when its current in-force plan year (or, past due,
 * its most recently ended year) has an end date and no successor year exists.
 * A group drops off the moment someone creates its next plan year.
 */

import type { OpsGroup } from '@entities/ops-group';
import type { ContractYear } from '@entities/contract-year';
import { groupLifecycle } from '@entities/ops-group';

export interface RenewalQueueEntry {
  group: OpsGroup;
  queueYear: ContractYear;
  daysUntilEnd: number;
}

export function todayStr(): string {
  return new Date().toISOString().split('T')[0]!;
}

export function daysBetween(from: string, to: string): number {
  const a = new Date(`${from.split('T')[0]}T00:00:00`);
  const b = new Date(`${to.split('T')[0]}T00:00:00`);
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
}

export function buildRenewalQueue(
  groups: OpsGroup[],
  allYears: ContractYear[],
  today: string = todayStr(),
): RenewalQueueEntry[] {
  const yearsByAccount = new Map<string, ContractYear[]>();
  for (const cy of allYears) {
    const list = yearsByAccount.get(cy.accountId) ?? [];
    list.push(cy);
    yearsByAccount.set(cy.accountId, list);
  }

  const result: RenewalQueueEntry[] = [];
  for (const group of groups) {
    const years = yearsByAccount.get(group.accountId) ?? [];
    if (years.length === 0) continue;

    // Current in-force year; when there is none, fall back to the most
    // recently ENDED year so past-due groups (year ended, nobody renewed)
    // surface in the queue instead of silently vanishing.
    const inForce = years.find((y) => {
      if (!y.startDate || !y.endDate) return false;
      const s = y.startDate.split('T')[0]!;
      const e = y.endDate.split('T')[0]!;
      return s <= today && today <= e;
    });
    const queueYear = inForce?.endDate
      ? inForce
      : years
          .filter((y) => y.startDate && y.endDate && y.endDate.split('T')[0]! < today)
          .sort((a, b) => b.endDate!.localeCompare(a.endDate!))[0];
    if (!queueYear?.endDate) continue;

    // A successor year (any year starting after this one ends) means someone
    // already renewed, so the group leaves the queue.
    const endStr = queueYear.endDate.split('T')[0]!;
    const hasSuccessor = years.some((y) => {
      if (y.id === queueYear.id || !y.startDate) return false;
      return y.startDate.split('T')[0]! > endStr;
    });
    if (hasSuccessor) continue;

    const daysUntilEnd = daysBetween(today, endStr);
    if (!Number.isFinite(daysUntilEnd)) continue; // corrupted date strings

    result.push({ group, queueYear, daysUntilEnd });
  }
  return result;
}

/**
 * Nav-badge count: queue entries excluding off-book (termed or archived)
 * groups, which the page hides behind its Show-termed switch, and limited to
 * the Past due and 0-30 buckets (Sasha 9/01, refining ADO #1597): the badge
 * is a call to action, and 400+ renewals due someday drowned out the 36 due
 * now. The 30-day line matches the page's own bucket edge in getDaysBucket.
 */
export function countRenewalQueue(groups: OpsGroup[], allYears: ContractYear[]): number {
  return buildRenewalQueue(groups, allYears)
    .filter((e) => !groupLifecycle(e.group).offBook && e.daysUntilEnd <= 30).length;
}
